import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronUp, ChevronDown, CheckCircle, AlertCircle, X } from 'lucide-react';
import { triggerPiepeline } from '../../services/api/virtualAssistant';
import { WebsiteData } from "../../types/CourseData";
import PipelineStatus from "../../components/PipelineStatus";

interface Pipeline {
  id: string;
  run_type: string;
  start_ts: string;
  status: string;
  end_ts: string;  
  docs_processed_count: number;
  docs_failed_count: number;
  message: string;
  failedDocuments: [];
}

interface WebsiteFooterProps {
  websites: WebsiteData[];
  currentStatus: null | {
    progress: number;
    status: string;
    error?: string;
  };
  setCurrentStatus: React.Dispatch<React.SetStateAction<null | {
    progress: number;
    status: string;
    error?: string;
  }>>;
  pipelineData: Pipeline[];
  pendingSave: boolean;
  setPendingSave: React.Dispatch<React.SetStateAction<boolean>>;
  isWebsiteRNightly: boolean;
  setIsWebsiteRNightly: React.Dispatch<React.SetStateAction<boolean>>;
  isWebsiteFullLoad: boolean;
  setIsWebsiteFullLoad: React.Dispatch<React.SetStateAction<boolean>>;
  assistant: any;
  systemConfig: { Id: string; ConfigValue: string; }[];
  triggerAutoSave: () => void;
  setModalRefreshNModal: React.Dispatch<React.SetStateAction<boolean>>;
  fetchPipelineData: () => void;
  setGetDataClickTime: React.Dispatch<React.SetStateAction<Date | null>>;
  lastSaveTarget: 'azure' | 'azure';
  onCancelPipeline: () => void;
  cancellingPipeline: boolean;
  gettingData: boolean;
  setGettingData: React.Dispatch<React.SetStateAction<boolean>>;
}

const WebsiteFooter: React.FC<WebsiteFooterProps> = ({
  websites,
  currentStatus,
  setCurrentStatus,
  pipelineData,
  pendingSave,
  setPendingSave,
  isWebsiteRNightly,
  setIsWebsiteRNightly,
  isWebsiteFullLoad,
  setIsWebsiteFullLoad,
  assistant,
  systemConfig,
  triggerAutoSave,
  setModalRefreshNModal,
  fetchPipelineData,
  setGetDataClickTime,
  lastSaveTarget,
  onCancelPipeline,
  cancellingPipeline,
  gettingData,
  setGettingData
}) => {
  const [expandedGetData, setExpandedGetData] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const expandedSectionRef = useRef<HTMLDivElement | null>(null);
  // Keep fetchPipelineData ref current to avoid stale closure in setTimeout
  const fetchPipelineDataRef = useRef(fetchPipelineData);
  useEffect(() => {
    fetchPipelineDataRef.current = fetchPipelineData;
  }, [fetchPipelineData]);

  const toggleExpandRow = (idx: number) => {
    setExpandedRows(prev => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  // Status checks
  const isInProgress = currentStatus?.status === 'In Progress';
  const isCancelling = currentStatus?.status === 'Cancelling' || cancellingPipeline;
  const hasConnectedWebsites = websites && websites.some(w => (w.connectionState === 'connected' && w.status === 'connected'));
  const shouldShowSaveButton = hasConnectedWebsites || pendingSave;
  const showProgressBar = currentStatus?.status !== '' && hasConnectedWebsites;

  // Scroll to the expanded section when expandedGetData becomes true
  useEffect(() => {
    if (expandedGetData && expandedSectionRef.current) {
      expandedSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [expandedGetData]);

  const handleGetDataNow = async (type: 'azure' | 'azure') => {
    setGettingData(true);
    setPendingSave(false);
    setGetDataClickTime(new Date());
    setCurrentStatus({ progress: 20, status: 'In Progress' });

    let queue_name = systemConfig.find(item => item.Id === 'website_queue_name')?.ConfigValue ?? '';
    if (type === 'azure') {
      queue_name = systemConfig.find(item => item.Id === 'website_queue_name')?.ConfigValue ?? '';
    }

    const run_type = 'Adhoc';
    const full_load_value = String(isWebsiteFullLoad) === 'true' ? 'false' : 'true';

    if (queue_name) {
      await triggerPiepeline(assistant.app_id, run_type, queue_name, full_load_value);
      setTimeout(() => {
        fetchPipelineDataRef.current();
      }, 30000);
    }
  };

  const handleChange = () => {
    if (!isWebsiteRNightly) {
      if (assistant?.app_publishstatus?.toUpperCase() === 'UNPUBLISHED') {
        setModalRefreshNModal(true);
      }
    }
    setIsWebsiteRNightly(!isWebsiteRNightly);
    triggerAutoSave();
  };

  const handleFullLoadChange = () => {
    setIsWebsiteFullLoad(!isWebsiteFullLoad);
    triggerAutoSave();
  };

  return (
    <div className="border-t border-gray-200 p-3">
      <div className="flex justify-between items-center">
        {/* Nightly Refresh Toggle */}
        <div className="mt-1 ml-2 flex items-center">
          <div 
            className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
              String(isWebsiteRNightly) === 'true' ? "bg-black" : "bg-white"
            }`} 
            onClick={handleChange}
          >
            {String(isWebsiteRNightly) === 'true' && (
              <Check className="h-4 w-4 text-white" />
            )}
          </div>
          <label className="pl-2 font-semibold text-black">Refresh Nightly </label>
        </div>
          
        {/* Progress Bar */}
        {showProgressBar && (
          <div className="flex justify-between items-center w-[50%]">
            <div className="flex flex-col w-full space-y-2">
              <div className="w-full h-1 bg-gray-200 rounded overflow-hidden relative">
                {currentStatus?.status === 'In Progress' ? (
                  <div className="absolute w-1/3 h-full bg-yellow-500 animate-indeterminate" />
                ) : currentStatus?.status === 'Cancelling' ? (
                  <div className="absolute w-1/3 h-full bg-orange-500 animate-indeterminate" />
                ) : currentStatus?.status === 'Complete' ? (
                  <div className="absolute w-full h-full bg-green-500" />
                ) : currentStatus?.status === 'Cancelled' ? (
                  <div className="absolute w-full h-full bg-gray-500" />
                ) : currentStatus?.status === 'Failed' ? (
                  <div className="absolute w-full h-full bg-red-500" />
                ) : null}
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  {gettingData && currentStatus?.status === 'Complete' && (
                    <CheckCircle className="text-green-600 w-5 h-5" />
                  )}
                  {gettingData && currentStatus?.status === 'Failed' && (
                    <AlertCircle className="text-red-600 w-5 h-5" />
                  )}
                  {(currentStatus?.status === 'Cancelled') && (
                    <X className="text-gray-600 w-5 h-5" />
                  )}
                  {(currentStatus?.status === 'Cancelling' || isCancelling) && (
                    <X className="text-orange-600 w-5 h-5" />
                  )}
                  
                  <span className="text-sm font-medium text-gray-700">
                    {currentStatus?.status === 'In Progress' ? 'In Progress...' : 
                     currentStatus?.status === 'Cancelling' || isCancelling ? 'Cancelling...' :
                     currentStatus?.status === 'Cancelled' ? 'Cancelled' :
                     currentStatus?.status || ''}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
                      
        {/* Save/Cancel Button and Expand Toggle */}
        <div className="flex items-center space-x-2">
          {shouldShowSaveButton && (
            <div className="flex flex-col items-center">
              <div className="relative inline-flex flex-col w-48">
                <div className="flex w-full">
                  {/* Show CANCEL button when in progress or cancelling */}
                  {(isInProgress || isCancelling) ? (
                    <button 
                      type="button"
                      className={`h-10 px-4 rounded-md text-sm font-medium text-white transition border flex-grow ${
                        isCancelling 
                          ? "bg-orange-600 border-orange-600 cursor-not-allowed" 
                          : "bg-red-600 hover:bg-red-700 border-red-600"
                      }`}
                      onClick={onCancelPipeline}
                      disabled={isCancelling}
                    >
                      {isCancelling ? 'CANCELLING...' : 'CANCEL'}
                    </button>
                  ) : (
                    /* Show SAVE button when not in progress */
                    <button
                      type="button"
                      className={`h-10 px-4 rounded-md text-sm font-medium text-white transition border border-black flex-grow ${
                        pendingSave ? 'pulsating-button' : 'bg-black hover:bg-gray-800'
                      }`}
                      onClick={() => handleGetDataNow(lastSaveTarget)}
                      disabled={false}
                    >
                      RUN
                    </button>
                  )}
                </div>
              </div>
              
              {/* Show different messages based on state */}
              {pendingSave && !isInProgress && !isCancelling && (
                <span className="font-semibold text-red-600 mt-2 text-xs tracking-wider">
                  Hit Save to get data
                </span>
              )}
              {(isInProgress && !isCancelling) && (
                <span className="font-semibold text-yellow-600 mt-2 text-xs tracking-wider">
                  Operation in progress
                </span>
              )}
              {isCancelling && (
                <span className="font-semibold text-orange-600 mt-2 text-xs tracking-wider">
                  Cancelling operation
                </span>
              )}
              {currentStatus?.status === 'Cancelled' && (
                <span className="font-semibold text-gray-600 mt-2 text-xs tracking-wider">
                  Operation cancelled
                </span>
              )}
            </div>
          )}
          
          <button
            onClick={() => setExpandedGetData(!expandedGetData)}
            className="text-sm text-gray-600 hover:text-black flex"
          >
            {expandedGetData ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>
   
      {/* Expanded Pipeline Status Section */}
      {expandedGetData && (
        <div ref={expandedSectionRef}>
          <PipelineStatus
            currentStatus={currentStatus}
            pipelineData={pipelineData}
            expandedRows={expandedRows}
            toggleExpandRow={toggleExpandRow}
            expandedGetData={expandedGetData}
            assistant={assistant} 
          />
        </div>
      )}
    </div>
  );
};

export default WebsiteFooter;